// src/components/PropertiesTable.tsx
import { Button, Checkbox, Icons, Input, Label } from '@commenda/kakashi';
import { SupportedPropertyType, TSchemaObject } from '@commenda/types';
import React from 'react';
import { v4 as uuidV4 } from 'uuid';
import { ArrayEditor } from './editors/ArrayEditor';
import { EnumEditor } from './editors/EnumEditor';
import { ObjectEditor } from './editors/ObjectEditor';
import TypeSelect from './ui/TypeSelect';

export const PropertiesTable = ({
  schemaObjects,
  onChange,
  depth = 0,
  showAdditionalProperties = false,
}: {
  schemaObjects: TSchemaObject[];
  onChange: (n: TSchemaObject[]) => void;
  depth?: number;
  showAdditionalProperties?: boolean;
}) => {
  const addProperty = () => {
    const newSchemaObject: TSchemaObject = {
      id: uuidV4(),
      name: 'field_1',
      type: SupportedPropertyType.STRING,
      required: false,
    };
    onChange([...(schemaObjects ?? []), newSchemaObject]);
  };

  const update = (id: string, patch: Partial<TSchemaObject>) => {
    onChange(
      schemaObjects.map((so) => (so.id === id ? { ...so, ...patch } : so))
    );
  };

  const remove = (id: string) =>
    onChange(schemaObjects.filter((so) => so.id !== id));

  /** Slot that renders a nested PropertiesTable without creating an import cycle */
  const renderNestedPropertiesTable = (
    childSchemaObjects: TSchemaObject[],
    onListChange: (list: TSchemaObject[]) => void
  ) => (
    <div className="mt-2 ml-2">
      <PropertiesTable
        schemaObjects={childSchemaObjects}
        onChange={onListChange}
        depth={depth + 1}
        showAdditionalProperties={showAdditionalProperties}
      />
    </div>
  );

  return (
    <div className="w-full">
      <div className="overflow-auto rounded-xl border text-white border-border-primary">
        <table className="min-w-full">
          <thead className="bg-button-primary-bg">
            <tr>
              <th className="text-left px-3 py-2 w-[30%] txt-small-regular">
                Name
              </th>
              <th className="text-left px-3 py-2 w-[16%] txt-small-regular">
                Type
              </th>
              <th className="text-left px-3 py-2 w-[10%] txt-small-regular">
                Required
              </th>
              <th className="text-left px-3 py-2 w-[10%] txt-small-regular">
                Allow Null
              </th>
              <th className="text-left px-3 py-2 w-[20%] txt-small-regular">
                Title
              </th>
              <th className="text-left px-3 py-2 txt-small-regular">
                Description
              </th>
              <th className="text-right px-3 py-2 w-[10%] txt-small-regular">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {(schemaObjects ?? []).map((eachSchemaObject) => (
              <React.Fragment key={eachSchemaObject.id}>
                <tr>
                  <td className="px-3 py-2 align-top">
                    <div
                      className="flex items-center gap-2"
                      style={{ paddingLeft: depth * 16 }}
                    >
                      <Input
                        value={eachSchemaObject.name}
                        onChange={(e) =>
                          update(eachSchemaObject.id, { name: e.target.value })
                        }
                        className="text-text-secondary txt-small-regular !px-3.5 !py-2.5"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <TypeSelect
                      value={eachSchemaObject.type}
                      onChange={(kind: SupportedPropertyType) =>
                        update(eachSchemaObject.id, { type: kind })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Label className="w-full h-full flex items-center justify-center">
                      <Checkbox
                        checked={eachSchemaObject.required}
                        onCheckedChange={(checked) =>
                          update(eachSchemaObject.id, {
                            required: checked === true,
                          })
                        }
                      />
                    </Label>
                  </td>
                  <td className="px-3 py-2">
                    <Label className="w-full h-full flex items-center justify-center">
                      <Checkbox
                        checked={eachSchemaObject.allowNull}
                        onCheckedChange={(checked) =>
                          update(eachSchemaObject.id, {
                            allowNull: checked === true,
                          })
                        }
                      />
                    </Label>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Input
                      className="text-text-secondary txt-small-regular !px-3.5 !py-2.5"
                      placeholder="Title (optional)"
                      value={eachSchemaObject.title ?? ''}
                      onChange={(e) =>
                        update(eachSchemaObject.id, {
                          title: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Input
                      className="text-text-secondary txt-small-regular !px-3.5 !py-2.5"
                      placeholder="Description (optional)"
                      value={eachSchemaObject.description ?? ''}
                      onChange={(e) =>
                        update(eachSchemaObject.id, {
                          description: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      title="Delete"
                      onClick={() => remove(eachSchemaObject.id)}
                    >
                      <Icons.Trash01 className="size-4" />
                    </Button>
                  </td>
                </tr>

                {[
                  SupportedPropertyType.ENUM,
                  SupportedPropertyType.ARRAY,
                  SupportedPropertyType.OBJECT,
                ].includes(eachSchemaObject.type) && (
                  <tr>
                    <td colSpan={6} className="px-3 pb-4">
                      <div className="mt-2">
                        {eachSchemaObject.type ===
                          SupportedPropertyType.ENUM && (
                          <EnumEditor
                            schemaObject={eachSchemaObject}
                            onChange={(next) =>
                              update(eachSchemaObject.id, next)
                            }
                          />
                        )}
                        {eachSchemaObject.type ===
                          SupportedPropertyType.ARRAY && (
                          <ArrayEditor
                            schemaObject={eachSchemaObject}
                            onChange={(next) =>
                              update(eachSchemaObject.id, next)
                            }
                            renderPropertiesTable={renderNestedPropertiesTable}
                            showAdditionalProperties={showAdditionalProperties}
                          />
                        )}
                        {eachSchemaObject.type ===
                          SupportedPropertyType.OBJECT && (
                          <ObjectEditor
                            schemaObject={eachSchemaObject}
                            onChange={(next) =>
                              update(eachSchemaObject.id, next)
                            }
                            renderPropertiesTable={renderNestedPropertiesTable}
                            showAdditionalProperties={showAdditionalProperties}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <Button size="sm" variant="secondary" onClick={addProperty}>
          + Add property
        </Button>
      </div>
    </div>
  );
};
